import { format } from "date-fns";
import { it } from 'date-fns/locale/it';
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from '../../../lib/redux/hooks';
import { SpinnerComponent } from "../../../lib/spinner";
import { ButtonComponent } from "../../../ui/molecules/button/newButton";
import { Calendar } from "../../../ui/molecules/calendar";
import { MadfarmInput } from "../../../ui/molecules/input/newInput";
import { MadfarmStaticSelect } from "../../../ui/molecules/select/newSelect/static";
import { PopupForm } from "../../../ui/organisms/popupForm";
import { SectionDivider } from "../../../ui/organisms/section/divider";
import { FormSection } from "../../../ui/organisms/section/form";
import { Province, Region, regioniProvinceMap, regions } from "../../../utils";
import { provinceComuniMap } from "../../../utils/listaComuni";
import { CompanyCenterDTO } from "../feature/companyCenter/dto";
import {
    resetCompanyCenterCreateError,
    setCompanyCenterUpdateRequest,
    setCompanyCenterUpdateRequestField,
    updateCompanyCenter,
    validateCompanyCenterCreateRequest
} from "../feature/companyCenter/slice";
import { getCustomerById } from "../feature/slice";

registerLocale('it', it)

type OptionType = { value: string; label: string };

interface Props {
    companyCenter: CompanyCenterDTO;
    closeHandler: () => void;
}

export function EditCompanyCenterModal(props: Props) {
    const { closeHandler, companyCenter } = props;

    const customerState = useAppSelector(state => state.customer)
    const companyCenterState = useAppSelector(state => state.companyCenter)
    const dispatch = useAppDispatch()

    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([])
    const [selectedRegion, setSelectedRegion] = useState<OptionType | null>(null)
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([])
    const [selectedProvince, setSelectedProvince] = useState<OptionType | null>(null)

    const regionOptions: OptionType[] = Array.from(regioniProvinceMap.keys()).map(r => ({ value: r, label: r }))

    // useEffect(() => {
    //     dispatch(getCompanyCenterById(selectedId))
    // }, [dispatch, selectedId])

    useEffect(() => {
        if (companyCenter) {
            dispatch(setCompanyCenterUpdateRequest(companyCenter))
            setSelectedRegion({ value: companyCenter.countryRegion, label: companyCenter.countryRegion })
            setSelectedProvince({ value: companyCenter.province, label: companyCenter.province })
        }
    }, [companyCenter])

    useEffect(() => {
        if (selectedRegion) {
            const found = regions.find((r: Region) => r.name === selectedRegion.value)
            setProvinceOptions(found
                ? found.provinces.map((p: Province) => ({ value: p.code, label: p.name }))
                : []
            )
        } else {
            setProvinceOptions([])
        }
    }, [selectedRegion])

    useEffect(() => {
        if (selectedProvince) {
            const comuni = provinceComuniMap.get(selectedProvince.value) || []
            setComuneOptions(comuni.map((c: string) => ({ value: c, label: c })))
        } else {
            setComuneOptions([])
        }
    }, [selectedProvince])

    const handleRegionChange = (opt: OptionType | null) => {
        setSelectedRegion(opt)
        setSelectedProvince(null)
    }

    const handleProvinceChange = (opt: OptionType | null) => setSelectedProvince(opt)

    const updateRequest = companyCenterState.companyCenterUpdateRequest

    const validateRequest = {
        countryRegion: updateRequest.countryRegion ?? "",
        province: updateRequest.province ?? "",
        city: updateRequest.city ?? "",
        zipCode: updateRequest.zipCode ?? "",
        address: updateRequest.address ?? "",
        latitude: updateRequest.latitude ?? null,
        longitude: updateRequest.longitude ?? null,
        referrer: updateRequest.referrer ?? null,
        phone: updateRequest.phone ?? "",
        email: updateRequest.email ?? "",
        pec: updateRequest.pec ?? "",
        activationDate: updateRequest.activationDate ?? null,
        deactivationDate: updateRequest.deactivationDate ?? null,
        active: updateRequest.active ?? false,
        description: updateRequest.description ?? "",
        customerId: customerState.getCustomerByIdResponse?.id ?? "",
        code: updateRequest.code ?? "",
    }

    return (
        <PopupForm title="Modifica centro aziendale" close={closeHandler}>
            {
                companyCenterState.companyCenterByIdStatus === 'loading' || companyCenterState.companyCenterUpdateStatus === 'loading'
                    ? <div className="p-6"><SpinnerComponent size={"small"} /></div>
                    : (
                        <div className="p-6 bg-white flex flex-col gap-6">
                            <FormSection>
                                <SectionDivider label="Identificazione" />
                                <div className="grid grid-cols-3 gap-4">
                                    <MadfarmInput
                                        required
                                        label="ID / Codice"
                                        placeholder="ID / Codice"
                                        supportingText={companyCenterState.companyCenterCreateErrors.code}
                                        error={!!companyCenterState.companyCenterCreateErrors.code}
                                        value={updateRequest.code ?? ""}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterUpdateRequestField({ field: 'code', value: text }))
                                            dispatch(resetCompanyCenterCreateError('code'))
                                        }}
                                    />
                                    <div className="col-span-2">
                                        <MadfarmInput
                                            required
                                            label="Descrizione"
                                            placeholder="Descrizione"
                                            supportingText={companyCenterState.companyCenterCreateErrors.description}
                                            error={!!companyCenterState.companyCenterCreateErrors.description}
                                            value={updateRequest.description ?? ""}
                                            onChangeText={(text) => {
                                                dispatch(setCompanyCenterUpdateRequestField({ field: 'description', value: text }))
                                                dispatch(resetCompanyCenterCreateError('description'))
                                            }}
                                        />
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection>
                                <SectionDivider label="Indirizzo" />
                                <MadfarmInput
                                    required
                                    label="Indirizzo"
                                    placeholder="Indirizzo"
                                    supportingText={companyCenterState.companyCenterCreateErrors.address}
                                    error={!!companyCenterState.companyCenterCreateErrors.address}
                                    value={updateRequest.address ?? ""}
                                    onChangeText={(text) => {
                                        dispatch(setCompanyCenterUpdateRequestField({ field: 'address', value: text }))
                                        dispatch(resetCompanyCenterCreateError('address'))
                                    }}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Regione"
                                        noOptionsMessage="Nessuna regione trovata"
                                        placeholder="Regione"
                                        error={!!companyCenterState.companyCenterCreateErrors.countryRegion}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.countryRegion}
                                        options={regionOptions}
                                        value={updateRequest.countryRegion ?? ""}
                                        onChange={(value) => {
                                            const option = value as OptionType | null
                                            const newValue = option ? option.value : null
                                            handleRegionChange(option)
                                            dispatch(setCompanyCenterUpdateRequestField({ field: 'countryRegion', value: newValue }))
                                            dispatch(resetCompanyCenterCreateError('countryRegion'))
                                        }}
                                    />
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Provincia"
                                        noOptionsMessage="Nessuna provincia trovata"
                                        placeholder="Provincia"
                                        error={!!companyCenterState.companyCenterCreateErrors.province}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.province}
                                        options={provinceOptions}
                                        value={updateRequest.province ?? ""}
                                        onChange={(value) => {
                                            const option = value as OptionType | null
                                            const newValue = option ? option.value : null
                                            handleProvinceChange(option)
                                            dispatch(setCompanyCenterUpdateRequestField({ field: 'province', value: newValue }))
                                            dispatch(resetCompanyCenterCreateError('province'))
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Comune"
                                        noOptionsMessage="Nessun comune trovato"
                                        placeholder="Comune"
                                        error={!!companyCenterState.companyCenterCreateErrors.city}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.city}
                                        options={comuneOptions}
                                        value={updateRequest.city ?? ""}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            dispatch(setCompanyCenterUpdateRequestField({ field: 'city', value: newValue }))
                                            dispatch(resetCompanyCenterCreateError('city'))
                                        }}
                                    />
                                    <MadfarmInput
                                        required
                                        label="CAP"
                                        placeholder="CAP"
                                        supportingText={companyCenterState.companyCenterCreateErrors.zipCode}
                                        error={!!companyCenterState.companyCenterCreateErrors.zipCode}
                                        value={updateRequest.zipCode ?? ""}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterUpdateRequestField({ field: 'zipCode', value: text }))
                                            dispatch(resetCompanyCenterCreateError('zipCode'))
                                        }}
                                    />
                                </div>
                            </FormSection>

                            <FormSection>
                                <SectionDivider label="Geolocalizzazione" />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Latitudine"
                                        placeholder="Latitudine"
                                        value={(updateRequest.latitude as any) ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'latitude', value: text }))}
                                    />
                                    <MadfarmInput
                                        label="Longitudine"
                                        placeholder="Longitudine"
                                        value={(updateRequest.longitude as any) ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'longitude', value: text }))}
                                    />
                                </div>
                            </FormSection>

                            <FormSection>
                                <SectionDivider label="Contatti" />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Referente"
                                        placeholder="Referente"
                                        value={(updateRequest.referrer as any) ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'referrer', value: text }))}
                                    />
                                    <MadfarmInput
                                        label="Recapito"
                                        placeholder="Recapito"
                                        value={updateRequest.phone ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'phone', value: text }))}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Email"
                                        placeholder="Email"
                                        value={updateRequest.email ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'email', value: text }))}
                                    />
                                    <MadfarmInput
                                        label="PEC"
                                        placeholder="PEC"
                                        value={updateRequest.pec ?? ""}
                                        onChangeText={(text) => dispatch(setCompanyCenterUpdateRequestField({ field: 'pec', value: text }))}
                                    />
                                </div>
                            </FormSection>

                            <FormSection>
                                <SectionDivider label="Date" />
                                <div className="grid grid-cols-2 gap-4">
                                    <Calendar
                                        required
                                        errorLabel={companyCenterState.companyCenterCreateErrors.activationDate}
                                        label="Data Ingresso"
                                        selected={updateRequest.activationDate as any}
                                        onChange={(date) => {
                                            dispatch(setCompanyCenterUpdateRequestField({
                                                field: 'activationDate',
                                                value: format(new Date(date), 'yyyy-MM-dd')
                                            }))
                                            dispatch(resetCompanyCenterCreateError('activationDate'))
                                        }}
                                    />
                                    <Calendar
                                        label="Data Uscita"
                                        selected={updateRequest.deactivationDate as any}
                                        onChange={(date) => {
                                            dispatch(setCompanyCenterUpdateRequestField({
                                                field: 'deactivationDate',
                                                value: format(new Date(date), 'yyyy-MM-dd')
                                            }))
                                        }}
                                    />
                                </div>
                            </FormSection>

                            <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
                                <ButtonComponent
                                    size="md"
                                    iconPosition="off"
                                    variant="outline"
                                    color="blue"
                                    label="Annulla"
                                    onClick={closeHandler}
                                />
                                <ButtonComponent
                                    size="md"
                                    iconPosition="off"
                                    variant="solid"
                                    color="orange"
                                    label="Salva"
                                    onClick={() => {
                                        dispatch(validateCompanyCenterCreateRequest(validateRequest))
                                            .unwrap()
                                            .then(() => dispatch(updateCompanyCenter({ id: companyCenter.id, data: updateRequest })).unwrap())
                                            .then(() => { dispatch(getCustomerById(customerState.getCustomerByIdResponse?.id ?? "")); closeHandler() })
                                            .catch(() => { })
                                    }}
                                />
                            </div>
                        </div>
                    )
            }
        </PopupForm>
    )
}
